import React, { useRef, useState } from "react";

  const NAV_LINKS = [
    { href: "/calculator/", label: "Calculator" },
    { href: "/high-altitude-baking-chart/", label: "Baking Chart" },
    { href: "/high-altitude-cooking-chart/", label: "Cooking Chart" },
    { href: "/recipes/", label: "Recipes" },
    { href: "/blog/", label: "Blog" },
    { href: "/about/", label: "About" },
  ];

  const Segmented = ({ label, options, metricActive, onSwitch }) => (
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-bold text-softbrown">{label}</span>
        <div className="flex rounded-full border border-rule overflow-hidden">
          {options.map((opt, i) => {
            const isMetric = i === 1;
            const active = metricActive === isMetric;
            return (
              <button
                key={opt}
                onClick={() => !active && onSwitch()}
                className={`px-3 py-1 text-xs font-bold transition-colors duration-150 ${
                  active ? "bg-terracotta text-white" : "bg-cardcream text-softbrown hover:text-terracotta"
                }`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    );

  const Field = ({ label, value, onChange, placeholder, className = "" }) => (
      <div className={className}>
        <label className="block text-xs font-bold text-softbrown mb-1">{label}</label>
        <input
          type="number"
          inputMode="decimal"
          value={value}
          onChange={onChange}
          className="block w-full px-3.5 py-2.5 text-base text-ink bg-cream border border-rule rounded-xl focus:ring-terracotta focus:border-terracotta transition-shadow duration-150"
          placeholder={placeholder}
        />
      </div>
    );


  const HighAltitudeBakingTool = () => {
    const [targetAltitude, setTargetAltitude] = useState("");
    const [locating, setLocating] = useState(false);
    const [locateNote, setLocateNote] = useState(null);

    // Estimate the user's altitude from their location: browser geolocation ->
    // open-meteo elevation (free, keyless, CORS-friendly). User-initiated only,
    // never an on-load permission prompt; every failure degrades to the manual
    // field with an honest note.
    const useMyLocation = () => {
      if (locating) return;
      if (!navigator.geolocation) {
        setLocateNote('Location is not available in this browser. Type your altitude, or search "elevation of" plus your town.');
        return;
      }
      setLocating(true);
      setLocateNote(null);
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const { latitude, longitude } = pos.coords;
            const r = await fetch(`https://api.open-meteo.com/v1/elevation?latitude=${latitude}&longitude=${longitude}`);
            const j = await r.json();
            const meters = Array.isArray(j.elevation) ? j.elevation[0] : j.elevation;
            if (typeof meters !== "number") throw new Error("no elevation");
            const feet = Math.max(0, Math.round((meters * 3.28084) / 10) * 10);
            setTargetAltitude(String(feet));
            setLocateNote(`Estimated from your location: about ${feet.toLocaleString()} feet. Adjust if you know better.`);
          } catch (err) {
            setLocateNote('Could not estimate your altitude just now. Type it in, or search "elevation of" plus your town.');
          } finally {
            setLocating(false);
          }
        },
        () => {
          setLocating(false);
          setLocateNote('Location was blocked, no problem. Type your altitude, or search "elevation of" plus your town.');
        },
        { timeout: 8000, maximumAge: 600000 }
      );
    };
    const [flour, setFlour] = useState("");
    const [sugar, setSugar] = useState("");
    const [liquid, setLiquid] = useState("");
    const [leavening, setLeavening] = useState("");
    const [ovenTemp, setOvenTemp] = useState("");
    const [bakingTime, setBakingTime] = useState("");
    const [adjustedRecipe, setAdjustedRecipe] = useState(null);
    const [useDryMetric, setUseDryMetric] = useState(true);
    const [useWetMetric, setUseWetMetric] = useState(true);
    const resultsRef = useRef(null);

    const convertDry = (value, toMetric) => {
      return toMetric ? value * 120 : value / 120;
    };

    const convertWet = (value, toMetric) => {
      return toMetric ? value * 240 : value / 240;
    };

    const handleDryToggle = () => {
      setUseDryMetric(!useDryMetric);
      setFlour(prevFlour => convertDry(prevFlour, !useDryMetric).toFixed(2));
      setSugar(prevSugar => convertDry(prevSugar, !useDryMetric).toFixed(2));
      if (adjustedRecipe) {
        setAdjustedRecipe(prev => ({
          ...prev,
          flour: convertDry(prev.flour, !useDryMetric).toFixed(2),
          sugar: convertDry(prev.sugar, !useDryMetric).toFixed(2),
        }));
      }
    };

    const handleWetToggle = () => {
      setUseWetMetric(!useWetMetric);
      setLiquid(prevLiquid => convertWet(prevLiquid, !useWetMetric).toFixed(2));
      if (adjustedRecipe) {
        setAdjustedRecipe(prev => ({
          ...prev,
          liquid: convertWet(prev.liquid, !useWetMetric).toFixed(2),
        }));
      }
    };

    const calculateAdjustments = () => {
      const feetAbove3000 = Math.max(0, parseInt(targetAltitude) - 3000);
      const feetAbove3500 = Math.max(0, parseInt(targetAltitude) - 3500);

      const adjustedFlour = parseFloat(flour) + (feetAbove3500 / 3500) * (parseFloat(flour) / 16);
      const adjustedSugar = parseFloat(sugar) - (feetAbove3500 / 3500) * (parseFloat(sugar) / 16);
      const adjustedLiquid = parseFloat(liquid) + (parseInt(targetAltitude) / 1000) * (useWetMetric ? 15 : 0.0625);
      const adjustedLeavening = parseFloat(leavening) * (1 - (feetAbove3500 / 1000) * 0.15);
      const adjustedOvenTemp = parseInt(ovenTemp) + (feetAbove3000 / 1000) * 5;
      const adjustedBakingTime = parseFloat(bakingTime) * (1 - (parseInt(targetAltitude) / 1000) * 0.05);

      setAdjustedRecipe({
        flour: adjustedFlour.toFixed(2),
        sugar: adjustedSugar.toFixed(2),
        liquid: adjustedLiquid.toFixed(2),
        leavening: adjustedLeavening.toFixed(2),
        ovenTemp: adjustedOvenTemp.toFixed(0),
        bakingTime: adjustedBakingTime.toFixed(0),
      });
      // On small screens the results panel sits below the form; bring it into
      // view so the answer is never hidden after tapping the button.
      setTimeout(() => {
        if (window.innerWidth < 1024 && resultsRef.current) {
          resultsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 60);
    };

    const resultRows = adjustedRecipe
      ? [
          ["Flour", `${adjustedRecipe.flour} ${useDryMetric ? "g" : "cups"}`],
          ["Sugar", `${adjustedRecipe.sugar} ${useDryMetric ? "g" : "cups"}`],
          ["Liquid", `${adjustedRecipe.liquid} ${useWetMetric ? "mL" : "cups"}`],
          ["Leavening", `${adjustedRecipe.leavening} tsp`],
          ["Oven", `${adjustedRecipe.ovenTemp}°F`],
          ["Time", `${adjustedRecipe.bakingTime} min`],
        ]
      : null;

    return (
      <div className="min-h-screen bg-cream font-body text-ink">
        <header className="max-w-5xl mx-auto flex flex-wrap items-center justify-center sm:justify-between gap-x-4 gap-y-2 px-4 py-4">
          <a href="/" className="font-display font-bold text-lg text-ink no-underline">
            High Elevation Baking
          </a>
          <nav className="flex flex-wrap justify-center gap-x-4 gap-y-1">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-sm font-bold text-softbrown hover:text-terracotta no-underline transition-colors duration-150"
              >
                {l.label}
              </a>
            ))}
          </nav>
        </header>
        <hr className="max-w-5xl mx-auto border-rule" />

        <div className="w-full max-w-4xl mx-auto px-4 pb-10">
          <div className="text-center pt-5 pb-4 animate-fade-up">
            <h1 className="font-display text-2xl sm:text-3xl font-bold">
              High Altitude Baking Calculator
            </h1>
            <p className="text-softbrown text-sm sm:text-base mt-1">
              Your sea-level recipe in, altitude-ready amounts out.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 items-start">
            <div className="lg:col-span-3 bg-cardcream border border-rule p-5 rounded-2xl animate-fade-up-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Field
                    label="Target altitude (feet)"
                    value={targetAltitude}
                    onChange={(e) => setTargetAltitude(e.target.value)}
                    placeholder="e.g., 5000"
                  />
                  <div className="mt-1.5 flex items-baseline gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={useMyLocation}
                      disabled={locating}
                      className="text-sm font-bold text-terracotta hover:text-terradark disabled:opacity-60 transition-colors"
                    >
                      {locating ? "Locating..." : "\uD83D\uDCCD Use my location"}
                    </button>
                    {locateNote && (
                      <span className="text-xs text-softbrown">{locateNote}</span>
                    )}
                  </div>
                </div>
                <Field
                  label={`Flour ${useDryMetric ? "(g)" : "(cups)"}`}
                  value={flour}
                  onChange={(e) => setFlour(e.target.value)}
                  placeholder={useDryMetric ? "240" : "2"}
                />
                <Field
                  label={`Sugar ${useDryMetric ? "(g)" : "(cups)"}`}
                  value={sugar}
                  onChange={(e) => setSugar(e.target.value)}
                  placeholder={useDryMetric ? "200" : "1"}
                />
                <Field
                  label={`Liquid ${useWetMetric ? "(mL)" : "(cups)"}`}
                  value={liquid}
                  onChange={(e) => setLiquid(e.target.value)}
                  placeholder={useWetMetric ? "120" : "0.5"}
                />
                <Field
                  label="Leavening (tsp)"
                  value={leavening}
                  onChange={(e) => setLeavening(e.target.value)}
                  placeholder="1"
                />
                <Field
                  label="Oven temp (°F)"
                  value={ovenTemp}
                  onChange={(e) => setOvenTemp(e.target.value)}
                  placeholder="350"
                />
                <Field
                  label="Baking time (min)"
                  value={bakingTime}
                  onChange={(e) => setBakingTime(e.target.value)}
                  placeholder="30"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-6 mt-4">
                <Segmented
                  label="Dry"
                  options={["cups", "grams"]}
                  metricActive={useDryMetric}
                  onSwitch={handleDryToggle}
                />
                <Segmented
                  label="Wet"
                  options={["cups", "mL"]}
                  metricActive={useWetMetric}
                  onSwitch={handleWetToggle}
                />
              </div>

              <button
                onClick={calculateAdjustments}
                className="mt-4 w-full rounded-full py-3 px-8 text-base font-bold text-white bg-terracotta hover:bg-terradark hover:-translate-y-0.5 active:translate-y-0 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-terracotta focus:ring-offset-2"
              >
                Adjust my recipe
              </button>
            </div>

            <div
              ref={resultsRef}
              className={`lg:col-span-2 bg-cardcream border border-rule p-5 rounded-2xl scroll-mt-4 animate-fade-up-2 ${
                resultRows ? "lg:sticky lg:top-5" : "lg:self-stretch flex flex-col"
              }`}
            >
              <h2 className="font-display text-lg font-semibold mb-3">
                Adjusted for your altitude
              </h2>
              {resultRows ? (
                <div key={JSON.stringify(adjustedRecipe)} className="animate-fade-up">
                  {resultRows.map(([k, v]) => (
                    <div
                      key={k}
                      className="flex items-baseline justify-between border-b border-rule py-2 last:border-b-0"
                    >
                      <span className="font-semibold text-softbrown text-sm">{k}</span>
                      <span className="font-bold tabular-nums">{v}</span>
                    </div>
                  ))}
                  <p className="text-xs text-softbrown pt-3">
                    Baking something specific? Try our{" "}
                    <a href="/recipes/" className="text-terracotta font-bold">
                      pre-adjusted recipes
                    </a>
                    .
                  </p>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center py-10 lg:py-0">
                  <p className="text-softbrown text-sm text-center max-w-[26ch]">
                    Fill in the recipe and tap adjust. Not sure of your altitude?
                    Tap "Use my location" or search "elevation of" plus your town.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="mt-8 bg-cardcream border border-rule p-6 rounded-2xl">
            <h2 className="font-display text-xl font-semibold mb-4">
              Why does altitude change baking?
            </h2>
            <p className="text-softbrown">
              Above 3,000 feet, lower air pressure makes gases expand faster and
              liquids evaporate sooner. Left alone, cakes over-rise and collapse,
              cookies spread, and crumbs turn dry. The fix is small, opposite
              adjustments:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-2 text-softbrown">
              <li>A hotter oven, to set the structure before it over-expands</li>
              <li>A shorter bake, to keep things from drying out</li>
              <li>A little less sugar and a little more flour, for structure</li>
              <li>More liquid, to replace faster evaporation</li>
              <li>Less leavening, to control the rise</li>
            </ul>
            <p className="text-softbrown mt-3">
              Prefer to eyeball it? Use the{" "}
              <a href="/high-altitude-baking-chart/" className="text-terracotta font-bold">
                baking adjustment chart
              </a>{" "}
              or read{" "}
              <a href="/adjust-recipes/" className="text-terracotta font-bold">
                how to adjust any recipe
              </a>
              .
            </p>
          </div>
        </div>

        <div className="bg-butter/60 mt-8">
          <div className="max-w-4xl mx-auto px-4 py-7 flex flex-wrap items-center justify-between gap-5">
            <div>
              <div className="font-display font-bold text-xl">Get the free printable altitude chart</div>
              <div className="text-softbrown text-sm">Plus new high-altitude recipes as we publish them. No spam, ever.</div>
            </div>
            <form
              method="POST"
              action="https://app.blawgy.com/site-leads"
              className="flex flex-wrap gap-2"
            >
              <input type="hidden" name="site" value="highelevationbaking.com" />
              <input type="hidden" name="source" value="calculator" />
              <input type="hidden" name="redirect" value="https://highelevationbaking.com/chart-thanks/" />
              <input
                type="email"
                name="email"
                required
                placeholder="your@email.com"
                aria-label="Email address"
                className="px-4 py-2.5 border border-rule rounded-full text-base bg-cardcream text-ink min-w-[230px]"
              />
              <button
                type="submit"
                className="bg-terracotta hover:bg-terradark text-white font-bold px-5 py-2.5 rounded-full transition-colors duration-150"
              >
                Send it
              </button>
            </form>
          </div>
        </div>

        <footer className="text-center text-softbrown text-sm pb-10 px-4">
          <div className="space-x-4">
            <a href="/high-altitude-baking-chart/" className="hover:text-terracotta">Baking chart</a>
            <a href="/high-altitude-cooking-chart/" className="hover:text-terracotta">Cooking chart</a>
            <a href="/recipes/" className="hover:text-terracotta">Recipes</a>
            <a href="/blog/" className="hover:text-terracotta">Blog</a>
            <a href="/about/" className="hover:text-terracotta">About</a>
          </div>
          <div className="mt-3">Baked with ❤️ in Denver, CO</div>
        </footer>
      </div>
    );
  };
  export default HighAltitudeBakingTool;
